// frontend/pages/RequestWorkshop.js

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API_URL from '../api/baseURL';
import { trackPixel } from '../utils/metaPixel';
import SearchableSelect from '../components/SearchableSelect';
import { Page, Panel, Button, Field, inputClass, ErrorNote } from '../components/ui/Iparc';
import { signal } from '../theme/iparc';

const RequestWorkshop = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [form, setForm] = useState({ vehicleId: '', serviceType: '', date: '', workshopId: '' });
  const [coords, setCoords] = useState({ lat: 5.36, lng: -4.0 });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/users/vehicles`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setVehicles(Array.isArray(res.data) ? res.data : []))
      .catch(() => setVehicles([]));
  }, [token]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/users/workshops`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setWorkshops(Array.isArray(res.data) ? res.data : []))
      .catch(() => setWorkshops([]));
  }, [token]);

  useEffect(() => {
    navigator.geolocation?.getCurrentPosition(
      pos => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setCoords({ lat: 5.36, lng: -4.0 })
    );
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSending(true);
    try {
      await axios.post(`${API_URL}/requests`, {
        vehicleId: form.vehicleId,
        serviceType: form.serviceType,
        date: form.date,
        workshopId: form.workshopId,
        location: coords,
      }, { headers: { Authorization: `Bearer ${token}` } });
      trackPixel('Schedule', { content_name: form.serviceType || 'service request' });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || "Échec de l'envoi de la demande.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <Page title="Demande envoyée" subtitle="Atelier" width="narrow">
        <Panel>
          <div className="flex flex-col items-start gap-3 py-2">
            <span
              className="px-2 py-[2px] rounded-full text-[10px] font-semibold uppercase tracking-[.06em]"
              style={{ background: signal.okTint, color: signal.ok }}
            >
              Envoyée
            </span>
            <p className="text-[13px] text-[#C9C3B6] leading-[1.6]">
              Votre demande a été transmise à l’atelier. Vous recevrez un devis à
              consulter dans <strong className="text-[#F4F1EA]">Mes devis</strong>,
              où vous pourrez l’accepter pour lancer l’intervention.
            </p>
            <div className="flex gap-2.5 pt-1">
              <Button variant="primary" onClick={() => navigate('/owner/quotations')}>
                Voir mes devis
              </Button>
              <Button onClick={() => { setSent(false); setForm({ vehicleId: '', serviceType: '', date: '', workshopId: '' }); }}>
                Nouvelle demande
              </Button>
            </div>
          </div>
        </Panel>
      </Page>
    );
  }

  return (
    <Page title="Demander un service" subtitle="Atelier" width="narrow">
      <Panel title="Comment ça marche">
        <p className="text-[12.5px] text-[#8F877B] leading-[1.6]">
          Choisissez le véhicule, décrivez l’intervention et sélectionnez un atelier.
          L’atelier vous répond par un devis, que vous retrouverez dans{' '}
          <button
            type="button"
            onClick={() => navigate('/owner/quotations')}
            className="text-[#C9C3B6] underline underline-offset-2 hover:text-[#F4F1EA]"
          >
            Mes devis
          </button>{' '}
          pour l’accepter.
        </p>
      </Panel>

      {error && <ErrorNote>{error}</ErrorNote>}

      <Panel title="Votre demande">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <Field label="Véhicule">
            <SearchableSelect
              options={vehicles.map(v => ({
                value: v._id,
                label: `${v.brand || v.make || ''} ${v.model || ''} — ${v.plateNumber}`.trim(),
              }))}
              value={form.vehicleId}
              onChange={(val) => setForm({ ...form, vehicleId: val })}
              placeholder="Sélectionner un véhicule"
              required
            />
          </Field>

          <Field label="Type d’intervention" hint="Ex : vidange, freins, diagnostic…">
            <input
              type="text"
              required
              placeholder="Décrivez l’intervention"
              className={inputClass}
              value={form.serviceType}
              onChange={e => setForm({ ...form, serviceType: e.target.value })}
            />
          </Field>

          <Field label="Date souhaitée">
            <input
              type="datetime-local"
              required
              className={inputClass}
              value={form.date}
              onChange={e => setForm({ ...form, date: e.target.value })}
            />
          </Field>

          <Field label="Atelier">
            <SearchableSelect
              options={workshops.map(w => {
                const name = w.businessName || w.details?.businessName || w.name;
                const specs = w.specializations?.length ? ` · ${w.specializations.join(', ')}` : '';
                const brands = w.brands?.length ? ` · ${w.brands.join(', ')}` : '';
                return { value: w._id, label: `${name}${specs}${brands}` };
              })}
              value={form.workshopId}
              onChange={(val) => setForm({ ...form, workshopId: val })}
              placeholder="Sélectionner un atelier"
              required
            />
          </Field>

          <Button type="submit" variant="primary" disabled={sending} className="w-full h-[40px] mt-1">
            {sending ? 'Envoi…' : 'Envoyer la demande'}
          </Button>
        </form>
      </Panel>
    </Page>
  );
};

export default RequestWorkshop;
